import { useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@ts-core/lib/apollo';

import { getErrorMessage, useMutationCompletionHandler, useListReorder } from '@ts-core/lib/apollo';

import { getDocumentsBySubjectQuery } from '../graphql/queries';
import {
  updateDocumentMutation,
  toggleDocumentMutation,
  deleteDocumentMutation,
  sortDocumentMutation,
  importDocumentsFromCourseMutation,
} from '../graphql/mutations';
import { createExternalDocumentMutation } from '../graphql/mutations/createExternalDocumentMutation';

export default function useDocuments({ courseId, subjectId, onDocumentUpdated, onDocumentsImported }) {
  const { onError, removeItemFromCache, addItemToCache } = useMutationCompletionHandler();

  const {
    data,
    loading,
    error,
    refetch: refetchDocuments,
  } = useQuery(getDocumentsBySubjectQuery, {
    variables: { courseId, subjectId },
    skip: !courseId || !subjectId,
  });
  const documents = useMemo(() => data?.documents || [], [data]);

  /**
   * Update document
   */
  const [updateDocument, { loading: updatingDocument }] = useMutation(updateDocumentMutation, {
    onError,
    onCompleted: onDocumentUpdated,
  });
  const onUpdateDocument = useCallback(
    (input) => {
      updateDocument({
        variables: {
          courseId,
          subjectId,
          documentId: input._id,
          input,
        },
      });
    },
    [updateDocument, courseId, subjectId]
  );

  /**
   * Sort documents
   */
  const [sortDocuments] = useMutation(sortDocumentMutation, { onError });
  const { onReorderItems: onReorderDocuments } = useListReorder({
    query: getDocumentsBySubjectQuery,
    queryVariables: { courseId, subjectId },
    queryName: 'documents',
    onCallMutation: (from, to) => {
      sortDocuments({
        variables: { courseId, subjectId, input: { from, to } },
      });
    },
  });

  /**
   * Toggle document
   */
  const [toggleDocument] = useMutation(toggleDocumentMutation, { onError });
  const onToggleDocument = useCallback(
    (mDocument) => {
      const updatedDocument = { ...mDocument, enabled: !mDocument.enabled };
      toggleDocument({
        variables: {
          courseId,
          subjectId,
          documentId: mDocument._id,
          status: updatedDocument.enabled ? 'enable' : 'disable',
        },
        optimisticResponse: {
          document: updatedDocument,
        },
      });
    },
    [toggleDocument, courseId, subjectId]
  );

  /**
   * Delete document
   */
  const [deleteDocument] = useMutation(deleteDocumentMutation, {
    onError,
    update: removeItemFromCache({
      query: getDocumentsBySubjectQuery,
      variables: { courseId, subjectId },
      queryField: 'documents',
      mutationField: 'document',
    }),
  });
  const onDeleteDocument = useCallback(
    (mDocument) => {
      deleteDocument({
        variables: {
          courseId,
          subjectId,
          documentId: mDocument._id,
        },
        optimisticResponse: { document: mDocument },
      });
    },
    [courseId, subjectId, deleteDocument]
  );

  /**
   * Import documents from course
   */
  const [importDocuments, { loading: importingDocuments }] = useMutation(importDocumentsFromCourseMutation, {
    onError,
    onCompleted: onDocumentsImported,
    refetchQueries: refetchDocuments,
  });
  const onImportDocuments = useCallback(
    (input) => {
      importDocuments({ variables: { courseId, subjectId, input } });
    },
    [importDocuments, courseId, subjectId]
  );

  /**
   * Create external document
   */
  const [createExternalDocument, { loading: createExternalDocumentLoading, error: createExternalDocumentError }] = useMutation(
    createExternalDocumentMutation,
    {
      update: addItemToCache({
        query: getDocumentsBySubjectQuery,
        queryField: 'documents',
        variables: { courseId, subjectId },
        mutationField: 'createExternalDocument',
      }),
    }
  );
  const onCreateExternalDocument = useCallback(
    (input, callback) => {
      createExternalDocument({
        variables: {
          courseId,
          subjectId,
          input,
        },
      }).then(callback);
    },
    [createExternalDocument, courseId, subjectId]
  );

  return {
    loading,
    documents,
    error: getErrorMessage({ error }),

    onUpdateDocument,
    updatingDocument,

    onImportDocuments,
    importingDocuments,

    onDeleteDocument,

    onToggleDocument,

    onReorderDocuments,

    onCreateExternalDocument,
    createExternalDocumentLoading,
    createExternalDocumentError: getErrorMessage({
      error: createExternalDocumentError,
    }),
  };
}
